import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Car, Users, Shield, Clock, QrCode, MapPin, Star,
  CreditCard, Phone, BarChart3, ArrowRight, ChevronRight,
  Zap, Eye, Lock, Smartphone, Copy, Check, ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VENUE_ID = "venue_c028037a0ba3";

const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-[#D4AF37] transition-colors"
      data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
};

const FeatureCard = ({ icon: Icon, title, desc, color = "text-[#D4AF37]" }) => (
  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 hover:border-[#D4AF37]/20 transition-all group">
    <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center mb-3 group-hover:bg-[#D4AF37]/20 transition-colors">
      <Icon className={`w-5 h-5 ${color}`} />
    </div>
    <h3 className="text-white font-semibold text-sm mb-1">{title}</h3>
    <p className="text-gray-400 text-xs leading-relaxed">{desc}</p>
  </div>
);

const RoleCard = ({ role, email, password, description, features, link, linkLabel, accent }) => {
  const navigate = useNavigate();
  return (
    <div className={`bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 hover:border-${accent}/30 transition-all`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-white font-bold text-lg">{role}</h3>
          <p className="text-gray-400 text-sm mt-1">{description}</p>
        </div>
      </div>

      <div className="bg-white/[0.03] rounded-xl p-3 mb-4 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-500 text-[10px] uppercase tracking-wider">Email</p>
            <p className="text-white text-sm font-mono">{email}</p>
          </div>
          <CopyButton text={email} label={`${role} email`} />
        </div>
        <div className="border-t border-white/5 pt-2 flex items-center justify-between">
          <div>
            <p className="text-gray-500 text-[10px] uppercase tracking-wider">Password</p>
            <p className="text-white text-sm font-mono">{password}</p>
          </div>
          <CopyButton text={password} label={`${role} password`} />
        </div>
      </div>

      <ul className="space-y-1.5 mb-4">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-gray-300">
            <ChevronRight className="w-3 h-3 text-[#D4AF37] mt-0.5 shrink-0" />
            {f}
          </li>
        ))}
      </ul>

      <Button
        onClick={() => navigate(link)}
        className="w-full h-10 rounded-xl bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold text-sm"
        data-testid={`try-${role.toLowerCase().replace(/\s/g, '-')}-btn`}
      >
        {linkLabel} <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );
};

export default function DemoPage() {
  const navigate = useNavigate();
  const guestUrl = `${BACKEND_URL}/guest?venue=${VENUE_ID}`;

  return (
    <div className="min-h-screen bg-[#090A0F] text-white" data-testid="demo-page">
      {/* Header */}
      <div className="border-b border-white/5 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <img src="/logo.svg" alt="VeloPark" className="w-6 h-6 rounded-full" />
            <span className="font-bold text-lg tracking-wide">VeloPark</span>
            <span className="text-[10px] bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-0.5 rounded-full font-medium ml-1">DEMO</span>
          </div>
          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            className="text-gray-400 hover:text-white text-sm"
          >
            Back to Home
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 space-y-14">
        {/* Hero */}
        <div className="text-center space-y-4 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4AF37]/20 bg-[#D4AF37]/5 text-[#D4AF37] text-xs font-medium">
            <Eye className="w-3.5 h-3.5" />
            Interactive Product Demo
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight">
            Experience <span className="text-[#D4AF37]">VeloPark</span>
          </h1>
          <p className="text-gray-400 text-base sm:text-lg max-w-lg mx-auto">
            Try every feature live. Three dashboards, one seamless valet experience. No setup needed.
          </p>
        </div>

        {/* Quick Start: Guest QR Flow */}
        <div className="bg-gradient-to-br from-[#D4AF37]/10 to-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-2xl p-6 sm:p-8" data-testid="guest-flow-section">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="flex-1 space-y-4">
              <div className="flex items-center gap-2">
                <QrCode className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="text-xl font-bold text-white">Star Feature: QR Guest Mode</h2>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                The app-less experience. Guests scan a QR code at the venue, verify their phone, register their car, 
                and track retrieval with a live animated status page — all without downloading anything.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => window.open(`${BACKEND_URL}/guest?venue=${VENUE_ID}`, '_blank')}
                  className="h-11 rounded-xl bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold px-6"
                  data-testid="try-guest-mode-btn"
                >
                  <Smartphone className="w-4 h-4 mr-2" />
                  Try Guest Mode
                  <ExternalLink className="w-3.5 h-3.5 ml-2" />
                </Button>
                <div className="flex items-center gap-2 bg-black/30 rounded-xl px-4 py-2">
                  <p className="text-gray-400 text-xs font-mono truncate max-w-[260px]">{guestUrl}</p>
                  <CopyButton text={guestUrl} label="Guest URL" />
                </div>
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-gray-400 pt-2">
                <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> Phone OTP (demo code shown)</span>
                <span className="flex items-center gap-1"><Car className="w-3 h-3" /> Live animation</span>
                <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> No login needed</span>
              </div>
            </div>
            <div className="w-full sm:w-48 flex-shrink-0">
              <div className="bg-black/30 rounded-xl p-3 space-y-2 text-center">
                <p className="text-gray-500 text-[10px] uppercase tracking-wider">Guest Flow Steps</p>
                {["Scan QR Code", "Enter Phone", "Verify OTP", "Register Car", "Track & Request"].map((step, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="w-5 h-5 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center text-[10px] font-bold shrink-0">{i + 1}</span>
                    <span className="text-gray-300">{step}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Three Role Cards */}
        <div>
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold">Try Each Dashboard</h2>
            <p className="text-gray-400 text-sm mt-2">Login with test credentials to explore each role</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <RoleCard
              role="Admin"
              email="admin@velopark.com"
              password="admin123456"
              description="Full platform control"
              accent="[#D4AF37]"
              features={[
                "Manage venues, staff, parking spots",
                "Analytics: peak hours, revenue, trends",
                "Multi-venue overview dashboard",
                "QR code generation per venue",
                "Liability waiver management",
                "Customer ratings & reviews"
              ]}
              link="/login"
              linkLabel="Login as Admin"
            />
            <RoleCard
              role="Valet Staff"
              email="valet@test.com"
              password="valet123"
              description="On-ground operations"
              accent="amber"
              features={[
                "Real-time request feed (WebSocket)",
                "Walk-in guest registration",
                "Visual parking spot map",
                "Status progression management",
                "Privacy: phone numbers masked",
                "Estimated wait time display"
              ]}
              link="/login"
              linkLabel="Login as Valet"
            />
            <RoleCard
              role="Customer"
              email="customer@test.com"
              password="customer123"
              description="End-user experience"
              accent="emerald"
              features={[
                "One-tap car request",
                "Live status tracking",
                "Book a driver service",
                "Stripe payment checkout",
                "Rate completed services",
                "Real-time notifications"
              ]}
              link="/login"
              linkLabel="Login as Customer"
            />
          </div>
        </div>

        {/* Feature Grid */}
        <div>
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold">Platform Features</h2>
            <p className="text-gray-400 text-sm mt-2">Everything built into VeloPark</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <FeatureCard icon={Zap} title="One-Tap Request" desc="Guest taps a button, car arrives by exit" />
            <FeatureCard icon={QrCode} title="QR Check-in" desc="No app download, works instantly" />
            <FeatureCard icon={Clock} title="Real-Time Tracking" desc="WebSocket live status updates" />
            <FeatureCard icon={MapPin} title="Parking Grid" desc="Visual spot map for valet staff" />
            <FeatureCard icon={Shield} title="Liability Waivers" desc="Digital accept/decline compliance" />
            <FeatureCard icon={Lock} title="Privacy First" desc="Phone numbers masked for staff" />
            <FeatureCard icon={CreditCard} title="Stripe Payments" desc="Secure checkout for driver packages" />
            <FeatureCard icon={BarChart3} title="Analytics" desc="Peak hours, revenue, trends" />
            <FeatureCard icon={Star} title="Rating System" desc="Stars + reviews after service" />
            <FeatureCard icon={Users} title="Multi-Venue" desc="Aggregate metrics per venue" />
            <FeatureCard icon={Phone} title="3 Auth Methods" desc="Email, Google OAuth, Phone OTP" />
            <FeatureCard icon={Car} title="Valet Animation" desc="Live journey visualization" />
          </div>
        </div>

        {/* Tech Stack */}
        <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
          <h2 className="text-lg font-bold mb-4 text-center">Tech Stack</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {["React", "Tailwind CSS", "Shadcn/UI", "FastAPI", "MongoDB", "WebSocket", "Stripe", "JWT Auth", "Google OAuth", "Recharts"].map(tech => (
              <span key={tech} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300 text-xs font-medium">
                {tech}
              </span>
            ))}
          </div>
        </div>

        {/* Footer CTA */}
        <div className="text-center space-y-4 pb-8">
          <p className="text-gray-500 text-sm">Ready to explore?</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              onClick={() => window.open(`${BACKEND_URL}/guest?venue=${VENUE_ID}`, '_blank')}
              className="h-11 rounded-xl bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold px-6"
            >
              <QrCode className="w-4 h-4 mr-2" /> Try Guest Mode
            </Button>
            <Button
              onClick={() => navigate("/login")}
              variant="outline"
              className="h-11 rounded-xl border-white/10 text-white hover:bg-white/5 px-6"
            >
              <Users className="w-4 h-4 mr-2" /> Login to Dashboard
            </Button>
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="h-11 rounded-xl border-white/10 text-white hover:bg-white/5 px-6"
            >
              <ArrowRight className="w-4 h-4 mr-2" /> Landing Page
            </Button>
          </div>
          <p className="text-gray-600 text-xs pt-4">VeloPark — Your Car, Ready Before You Are.</p>
          <div className="flex items-center justify-center gap-3 text-xs text-gray-500">
            <a href="https://velopark.in" target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37] transition-colors">velopark.in</a>
            <span className="text-white/10">|</span>
            <a href="mailto:contact@velopark.in" className="hover:text-[#D4AF37] transition-colors">contact@velopark.in</a>
          </div>
        </div>
      </div>
    </div>
  );
}
